import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, MoreVertical, Pencil, Loader2, Cpu } from 'lucide-react';
import { machinesApi } from '@/api/machinesApi';
import { useToast } from '@/hooks/useToast';
import { MachineModal } from '@/components/catalog/MachineModal';
import type { Machine } from '@/types/api';

interface MachinesProps {
  embedded?: boolean;
}

export function Machines({ embedded = false }: MachinesProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Machine | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const { success, error } = useToast();
  const queryClient = useQueryClient();

  const { data: items, isLoading } = useQuery({
    queryKey: ['machines', 'all'],
    queryFn: () => machinesApi.getAll(true),
  });

  const saveMutation = useMutation({
    mutationFn: async (data: Parameters<typeof machinesApi.create>[0] & { id?: string }) => {
      if (data.id) {
        const { id, ...rest } = data;
        return machinesApi.update(id, rest);
      }
      return machinesApi.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: machinesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines'] });
      success('Machine Removed', 'Machine has been deactivated.', 4000);
    },
    onError: () => error('Failed', 'Could not remove machine.'),
  });

  const handleSave = async (data: Parameters<typeof machinesApi.create>[0] & { isActive?: boolean }) => {
    try {
      if (editingItem) {
        await saveMutation.mutateAsync({ ...data, id: editingItem.id });
        success('Machine Updated', 'Machine saved successfully.', 4000);
      } else {
        await saveMutation.mutateAsync(data);
        success('Machine Created', 'New machine added.', 4000);
      }
      setEditingItem(null);
    } catch {
      error('Failed', 'Could not save machine.');
      throw new Error('save failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {!embedded && (
          <div>
            <h1 className="font-display text-3xl font-bold text-gray-900 dark:text-warm-cream">Machines</h1>
            <p className="text-gray-600 dark:text-warm-sand mt-1">
              Shop equipment used for supply compatibility and production routing
            </p>
          </div>
        )}
        <button onClick={() => setShowModal(true)} className={`btn-primary ${embedded ? 'sm:ml-auto' : ''}`}>
          <Plus className="w-5 h-5 mr-2" />
          Add Machine
        </button>
      </div>

      {isLoading ? (
        <div className="card p-12 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-450" />
        </div>
      ) : !items?.length ? (
        <div className="card p-12 text-center text-gray-600 dark:text-warm-sand">
          <Cpu className="w-10 h-10 mx-auto mb-3 opacity-50" />
          No machines yet. Add your printers, lasers, cutters, and other equipment.
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-charcoal-850 text-left">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 w-12" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-gray-200 dark:border-charcoal-700">
                  <td className="px-4 py-3 font-medium">{item.name}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-warm-sand font-mono text-xs">{item.code}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${item.isActive ? 'bg-success-500/15 text-success-400' : 'bg-gray-500/15 text-gray-400'}`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 relative">
                    <button type="button" onClick={() => setActiveMenu(activeMenu === item.id ? null : item.id)} className="p-1 hover:bg-gray-100 dark:hover:bg-charcoal-700 rounded">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {activeMenu === item.id && (
                      <div className="absolute right-4 top-10 z-10 bg-white dark:bg-charcoal-800 border rounded-lg shadow-lg py-1 min-w-[120px]">
                        <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-charcoal-700 flex items-center gap-2" onClick={() => { setEditingItem(item); setShowModal(true); setActiveMenu(null); }}>
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                        {item.isActive && (
                          <button type="button" className="w-full px-3 py-2 text-left text-sm text-danger-400 hover:bg-gray-100 dark:hover:bg-charcoal-700" onClick={() => { deleteMutation.mutate(item.id); setActiveMenu(null); }}>
                            Deactivate
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <MachineModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingItem(null); }}
        onSave={handleSave}
        machine={editingItem}
      />
    </div>
  );
}
